"use client";

import { useState, useCallback } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { DataTable } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { MoreHorizontal, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import { Job } from "../types";
import { useConfigTranslation } from "../utils/configUtils";
import { useJobsManagement } from "@/components/common/JobsManagementBase";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";

interface JobTableProps {
  jobs: Job[];
}

export function JobTable({ jobs }: JobTableProps) {
  const { companyId, fetchJobs, setSelectedJob, setIsJobModalOpen } =
    useJobsManagement();
  const { toast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<Job | null>(null);
  const { getFacilityTypeLabel, getWorkingHoursLabel, getContractTypeLabel } =
    useConfigTranslation();

  const handleJobDelete = useCallback(
    async (job: Job) => {
      try {
        const { error } = await supabase.from("jobs").delete().eq("id", job.id);

        if (error) throw error;

        toast({
          title: "Erfolg",
          description: "Stelle erfolgreich gelöscht"
        });

        if (companyId) {
          fetchJobs(companyId);
        }
      } catch (error) {
        console.error("Error deleting job:", error);
        toast({
          title: "Fehler",
          description: "Fehler beim Löschen der Stelle",
          variant: "destructive"
        });
      }
    },
    [companyId, fetchJobs, toast]
  );

  const handleJobToggleActive = useCallback(
    async (job: Job) => {
      try {
        const { error } = await supabase
          .from("jobs")
          .update({
            is_active: !job.is_active,
            updated_at: new Date().toISOString()
          })
          .eq("id", job.id);

        if (error) throw error;

        toast({
          title: "Erfolg",
          description: `Stelle erfolgreich ${
            job.is_active ? "deaktiviert" : "aktiviert"
          }`
        });

        if (companyId) {
          fetchJobs(companyId);
        }
      } catch (error) {
        console.error("Error toggling job active status:", error);
        toast({
          title: "Fehler",
          description: "Fehler beim Ändern des Status",
          variant: "destructive"
        });
      }
    },
    [companyId, fetchJobs, toast]
  );

  const handleAddJob = useCallback(() => {
    setSelectedJob(undefined);
    setIsJobModalOpen(true);
  }, [setSelectedJob, setIsJobModalOpen]);

  const handleEditJob = useCallback(
    (job: Job) => {
      setSelectedJob(job);
      setIsJobModalOpen(true);
    },
    [setSelectedJob, setIsJobModalOpen]
  );

  const columns: ColumnDef<Job>[] = [
    {
      accessorKey: "title",
      header: "Position",
      cell: ({ row }) => {
        const job = row.original;
        return (
          <div>
            <div className="font-medium">{job.title}</div>
            <div className="text-sm text-gray-500">{job.company}</div>
          </div>
        );
      }
    },
    {
      accessorKey: "location",
      header: "Standort",
      cell: ({ row }) => {
        const location = row.getValue("location") as string;
        return <span>{location}</span>;
      }
    },
    {
      accessorKey: "working_hours",
      header: "Arbeitszeit",
      cell: ({ row }) => {
        const workingHours = row.getValue("working_hours") as string;
        return (
          <Badge variant="outline">{getWorkingHoursLabel(workingHours)}</Badge>
        );
      }
    },
    {
      accessorKey: "facility_type",
      header: "Einrichtungstyp",
      cell: ({ row }) => {
        const facilityType = row.getValue("facility_type") as string;
        return (
          <Badge variant="outline">{getFacilityTypeLabel(facilityType)}</Badge>
        );
      }
    },
    {
      accessorKey: "contract_type",
      header: "Vertragstyp",
      cell: ({ row }) => {
        const contractType = row.getValue("contract_type") as string;
        return contractType ? (
          <Badge variant="outline">{getContractTypeLabel(contractType)}</Badge>
        ) : (
          <span className="text-gray-400">-</span>
        );
      }
    },
    {
      accessorKey: "is_active",
      header: "Status",
      cell: ({ row }) => {
        const isActive = row.getValue("is_active") as boolean;
        return (
          <Badge variant={isActive ? "default" : "secondary"}>
            {isActive ? "Aktiv" : "Inaktiv"}
          </Badge>
        );
      }
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => {
        const job = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <MoreHorizontal size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditJob(job);
                }}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Bearbeiten
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleJobToggleActive(job);
                }}
              >
                {job.is_active ? (
                  <>
                    <EyeOff className="mr-2 h-4 w-4" />
                    Deaktivieren
                  </>
                ) : (
                  <>
                    <Eye className="mr-2 h-4 w-4" />
                    Aktivieren
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-600"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setJobToDelete(job);
                  setDeleteDialogOpen(true);
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Löschen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      }
    }
  ];

  const handleDelete = () => {
    if (jobToDelete) {
      handleJobDelete(jobToDelete);
      setDeleteDialogOpen(false);
      setJobToDelete(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Stellenangebote</h3>
        <Button onClick={handleAddJob}>Stelle hinzufügen</Button>
      </div>

      {jobs.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          Keine Stellenangebote gefunden
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={jobs}
          searchKey="title"
          filterPlaceholder="Stellenangebote filtern..."
        />
      )}

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Stelle löschen</DialogTitle>
            <DialogDescription>
              Bist du sicher, dass du diese Stelle löschen möchtest? Diese
              Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
